import { cn } from 'noddi-ui-common';
import { type CarProps, getCarDisplayName } from 'noddi-util';
import type { ReactNode } from 'react';
import { NavioTireSeasonChip } from '../../CarWheel/NavioTireSeasonChip';
import type { TireSeasonInput } from '../../CarWheel/tireSeasonStyle';
import { NavioLicensePlate } from './NavioLicensePlate';

interface Props {
  car: CarProps;
  /**
   * The season of the wheel set or tires on this row, as a box in front of the plate; `null` keeps an empty box so
   * plates stay aligned. Leave it out where the row has no season.
   */
  season?: TireSeasonInput | null;
  /** The line under the plate; defaults to make and model. */
  customDisplayName?: string | null;
  wrapDisplayName?: boolean;
  /** Shown in the license plate's place, for items stored without a car. */
  leading?: ReactNode;
  /** More lines under make and model (owner, location), lined up with the plate number. */
  children?: ReactNode;
}

/** The item cell of every table and list row about a car: season box, plate, and make + model under the plate number. */
export const CarInfoCell = ({ car, season, customDisplayName, wrapDisplayName, leading, children }: Props) => {
  const displayName =
    customDisplayName ?? (getCarDisplayName({ car, showLicensePlate: false, showYear: false }) || null);
  const hasSeasonBox = season !== undefined;

  return (
    <div className='flex flex-col items-start gap-1'>
      {leading ? (
        <span className='inline-flex items-center gap-2'>
          {hasSeasonBox && <NavioTireSeasonChip season={season} size='sm' iconOnly placeholder />}
          {leading}
        </span>
      ) : (
        <NavioLicensePlate car={car} season={season} />
      )}
      {displayName && (
        <span
          className={cn(
            'text-sm text-muted-foreground',
            // Under the plate number, past the 22px season box and its 8px gap.
            hasSeasonBox && 'pl-[30px]',
            wrapDisplayName ? 'max-w-36 break-words' : 'text-nowrap'
          )}
        >
          {displayName}
        </span>
      )}
      {children != null && (
        <div className={cn('flex flex-col text-xs text-muted-foreground', hasSeasonBox && 'pl-[30px]')}>{children}</div>
      )}
    </div>
  );
};
